import {
  AppNode,
  CategoricalNode,
  ContinuousNode,
  DiscreteNode,
  EdgeData,
  MixtureNode,
  NodeProperties,
  NodeType,
  TableNode,
  TableNodeProperties,
} from "@features/builder/graph/types"
import { FormProvider, useForm } from "react-hook-form"
import { FormHelperText, Grid, Tooltip, Typography } from "@mui/material"
import NodeTypeField from "@features/builder/NodeDrawer/NodeForm/NodeTypeField"
import PropertyField from "@features/builder/NodeDrawer/NodeForm/PropertyField"
import { InferType } from "yup"
import { getValidationSchema } from "@features/builder/NodeDrawer/NodeForm/schema"
import NodePropertiesAccordion from "@features/builder/NodeDrawer/NodeForm/NodePropertiesAccordion"
import { KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useState } from "react"
import ContinuousPropertiesTableAccordion from "@features/builder/NodeDrawer/NodeForm/ContinuousPropertiesTableAccordion"
import {
  convertNodeBasedOnFormValues,
  getCategoryDisplayNames,
  getNodeFormDefaultValues,
} from "@features/builder/NodeDrawer/NodeForm/helpers"
import { yupResolver } from "@hookform/resolvers/yup"
import { Edge, useNodes, useReactFlow } from "@xyflow/react"
import CategoricalPropertiesTableAccordion from "@features/builder/NodeDrawer/NodeForm/CategoricalPropertiesTableAccordion"
import { doesNodeHaveLagParent } from "@features/builder/helpers/nodes"
import {
  GraphNavChildrens,
  GraphNavGroup,
  GraphNavGroupsWithNodes,
  GraphNavParents,
} from "@features/builder/NodeDrawer/NodeForm/GraphNav"
import NodeExpressionAccordion from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/NodeExpressionAccordion"
import SampleValueAccordion from "@features/builder/NodeDrawer/NodeForm/SampleValueAccordion"
import { PropertyColorField } from "@features/builder/NodeDrawer/NodeForm/PropertyColorField"
import ForecastAccordion from "@features/builder/NodeDrawer/NodeForm/ForecastAccordion"
import ScenarioAccordion from "@features/builder/NodeDrawer/NodeForm/ScenarioAccordion"
import NodeTableAccordion from "@features/builder/NodeDrawer/NodeForm/NodeTableAccordion/NodeTableAccordion"
import DiscretePropertiesTableAccordion from "@features/builder/NodeDrawer/NodeForm/DiscretePropertiesTableAccordion"
import { LabelField } from "@features/builder/NodeDrawer/NodeForm/LabelField"
import { useDeepCompareEffect } from "@hooks/useDeepCompareEffect"
import { Warning } from "@mui/icons-material"
import { isLocked } from "@features/builder/hooks/socketIOHooks"
import { LagLabelField } from "@features/builder/NodeDrawer/NodeForm/LagLabelField"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { useCheckExpressionMutation } from "@store/services/editorService"
import { packExpressionRiskFile } from "@features/builder/graph/serviceUtils"
import { shortcuts } from "@features/keymap/shortcuts"
import { getDfCpdValidation } from "@features/builder/graph/nodes/validation/pure/getDfCpdValidationState"
import { hasChildNodes } from "@utils/node/getParentsList"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { scrollToFirstFieldError } from "@utils/form"
import useNodeLock from "@features/builder/hooks/useNodeLock"
import { useNodesParentsLinkedNodes } from "@features/builder/hooks/useNodeParentsLinkedNodes"
import NodeMnemonicTableAccordion from "@features/builder/NodeDrawer/NodeForm/NodeMnemonicTableAccordion/NodeMnemonicTableAccordion"
import TagsSuggestionsAlert from "@features/builder/NodeDrawer/NodeForm/TagsSuggestionsAlert"
import { useNodeBasedOnFormValues } from "@features/builder/NodeDrawer/NodeForm/useNodeBasedOnFormValues"
import NodeLinkDialog from "@components/NodeLink/NodeLinkDialog"

type NodeFormProps = {
  node: AppNode
  onSubmit: (updatedNode: AppNode) => void
  disabled: boolean
  onDirtyChange: (isDirty: boolean) => void
  onLinkedNodeChange?: (linkedNodeId: string | null) => void
}

export type NodeFormValues = InferType<ReturnType<typeof getValidationSchema>>

const NodeForm = ({ onDirtyChange, node, onLinkedNodeChange, ...props }: NodeFormProps) => {
  // this is optimistic state of node that we keep. It's needed because for example if the node
  // originally was continuous and user changes it's type to table, we want immediately show table-related
  // components and for that we need to have table node.
  const [updatedNode, setUpdatedNode] = useState(node)
  const { getNode } = useReactFlow<AppNode, Edge<EdgeData>>()
  const allNodes = useNodes<AppNode>()
  const graphDocumentData = useGraphDocumentData()
  const [checkExpression] = useCheckExpressionMutation()
  const { getNodeValidation } = useNodesValidation()
  const { setNodeValidation } = useNodesValidation()
  const nodeLock = useNodeLock({ nodeId: node.id })
  const parentsLinkedNodesMap = useNodesParentsLinkedNodes(node)

  const isDisabled = props.disabled || isLocked(nodeLock)

  const form = useForm<NodeFormValues>({
    defaultValues: getNodeFormDefaultValues(updatedNode, allNodes),
    resolver: yupResolver(getValidationSchema(updatedNode, allNodes)),
    disabled: isDisabled,
  })

  const nodeBasedOnForm = useNodeBasedOnFormValues({ nodeId: node.id, values: form.getValues() })

  const linkValue = form.watch("link")
  const hasLinkedNode = Boolean(linkValue)

  useEffect(() => {
    if (!onLinkedNodeChange) return

    onLinkedNodeChange(linkValue || null)
  }, [linkValue, onLinkedNodeChange])

  const {
    formState: { isDirty },
    reset,
  } = form

  // prevent not related node changes to invoke useEffect reseting form values
  const simplifiedNode: AppNode = useMemo(() => {
    const updatedNode = structuredClone(node)
    updatedNode.selected = false
    updatedNode.dragging = false
    updatedNode.resizing = false
    updatedNode.expandParent = false
    updatedNode.parentId = undefined
    updatedNode.measured = undefined
    updatedNode.className = undefined
    updatedNode.zIndex = undefined
    updatedNode.width = undefined
    updatedNode.height = undefined
    updatedNode.style = undefined
    updatedNode.extent = undefined
    updatedNode.position = { x: 0, y: 0 }
    updatedNode.data.nestingLevel = 1

    if (updatedNode.type !== NodeType.Group) {
      updatedNode.data.metaNode.groups = []
    }

    return updatedNode
  }, [node])

  useDeepCompareEffect(() => {
    setUpdatedNode(node)
    reset(getNodeFormDefaultValues(simplifiedNode, allNodes))
  }, [simplifiedNode])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleSubmit = async (formData: NodeFormValues) => {
    if (formData.expression) {
      try {
        const payload = await packExpressionRiskFile(
          formData.expression,
          node,
          allNodes,
          graphDocumentData,
        )
        await checkExpression(payload).unwrap()
      } catch (e) {
        // we don't block saving the form at this point because it will be handled as separate ticket
        //form.setError("expression", {
        //  type: "custom",
        //  message: (e as RTKErrorResponse).data.message,
        //})
      }
    }
    const convertedNode = convertNodeBasedOnFormValues(
      updatedNode,
      allNodes,
      formData,
      parentsLinkedNodesMap,
    )

    // Make sure to move over position and group from the most recent version of that node
    // as position or belonging to specific group may have change during filling in of the form
    const currentNode = getNode(convertedNode.id)
    if (!currentNode) return

    convertedNode.position = currentNode.position
    convertedNode.parentId = currentNode.parentId
    convertedNode.extent = currentNode.extent
    if (convertedNode.type !== NodeType.Group && currentNode.type !== NodeType.Group) {
      convertedNode.data.metaNode.groups = currentNode.data.metaNode.groups
    }

    switch (convertedNode.type) {
      case NodeType.Continuous:
      case NodeType.Discrete:
      case NodeType.Mixture:
        setNodeValidation(
          convertedNode.id,
          "DF_CPD_PROPERTIES",
          getDfCpdValidation(convertedNode.data.originalNode.properties.df_cpd, {
            hasChildren: hasChildNodes(convertedNode.id, allNodes),
          }),
        )
        break
      default:
        break
    }
    setUpdatedNode(convertedNode)
    reset(getNodeFormDefaultValues(convertedNode, allNodes))

    props.onSubmit(convertedNode)
  }

  const selectableNodeTypeOptions = [
    ...(node.type === NodeType.Empty ? [NodeType.Empty] : []),
    ...(node.type === NodeType.Mixture ? [NodeType.Mixture] : [NodeType.Continuous]),
    ...nodeTypeOptions,
  ]

  // possibly split to separate handlers if logic keeps getting more complex
  const handlePropertiesUpdate = (properties: NodeProperties) => {
    if (
      updatedNode.type !== NodeType.Categorical &&
      updatedNode.type !== NodeType.Continuous &&
      updatedNode.type !== NodeType.Discrete &&
      updatedNode.type !== NodeType.Mixture
    ) {
      return
    }

    const node = convertNodeBasedOnFormValues(
      updatedNode,
      allNodes,
      form.getValues(),
      parentsLinkedNodesMap,
    ) as CategoricalNode | ContinuousNode | DiscreteNode | MixtureNode

    node.data.originalNode.properties = properties

    const nextFormValues = getNodeFormDefaultValues(node, allNodes)

    if (node.type === NodeType.Categorical) {
      nextFormValues.categoryDisplayNames = getCategoryDisplayNames(
        node.data.originalNode.properties,
        form.getValues("categoryDisplayNames"),
      )
    }

    form.reset(nextFormValues, { keepDefaultValues: true })
    setUpdatedNode(node)
  }

  const handleTableAttributesUpdate = (properties: TableNodeProperties) => {
    if (
      updatedNode.type !== NodeType.Table &&
      updatedNode.type !== NodeType.ScenarioTable &&
      updatedNode.type !== NodeType.ForecastTable
    ) {
      return
    }

    const formNode = convertNodeBasedOnFormValues(
      updatedNode,
      allNodes,
      form.getValues(),
      parentsLinkedNodesMap,
    ) as TableNode
    formNode.data.originalAttribute.properties = properties // Keep this line just after convertNodeBasedOnFormValues() to override properties with most recent ones

    form.reset(getNodeFormDefaultValues(formNode, allNodes), { keepDefaultValues: true })
    setUpdatedNode(formNode)
  }

  const handleNodeTypeChange = () => {
    const convertedNode = convertNodeBasedOnFormValues(
      updatedNode,
      allNodes,
      form.getValues(),
      parentsLinkedNodesMap,
    )

    form.reset(getNodeFormDefaultValues(convertedNode, allNodes), { keepDefaultValues: true })
    setUpdatedNode(convertedNode)
  }
  const originalNodeUnitFormat =
    node.type === NodeType.Group ? "" : node.data.metaNode["Unit Format"] ?? ""

  const fields: JSX.Element[] = []
  const lagErrorMessages = getNodeValidation(updatedNode.id, "NODE_NAME_FORMAT")?.errors ?? []
  const lagValueErrorMessages = getNodeValidation(updatedNode.id, "LAG_VALUE")?.errors ?? []

  const labelFieldWithErrors = (
    <>
      <LabelField control={form.control} name="label" />

      {/* do not show tags suggestions for node types that do not allow adding tags */}
      {!hasLinkedNode &&
        updatedNode.type !== NodeType.ForecastTable &&
        updatedNode.type !== NodeType.ScenarioTable &&
        updatedNode.type !== NodeType.Group &&
        updatedNode.type !== NodeType.Empty && <TagsSuggestionsAlert />}

      {lagErrorMessages.map(({ msg }, index) => (
        <FormHelperText key={`${msg}-${index}`} error>
          - {msg}
        </FormHelperText>
      ))}
    </>
  )

  const nodeTypeField = (
    <NodeTypeField
      control={form.control}
      name="type"
      isOptionPresent={(type) => selectableNodeTypeOptions.includes(type)}
      onChange={handleNodeTypeChange}
      disabled={props.disabled || hasLinkedNode}
    />
  )

  const handleRemoveLink = () => {
    form.setValue("link", "", { shouldDirty: true, shouldValidate: true })
    setNodeValidation(node.id ?? "", "NODE_LINK", { status: "valid", errors: [] })
  }

  const handleSaveLink = (linkToSave: string) => {
    form.setValue("link", linkToSave, { shouldDirty: true, shouldValidate: true })
    form.trigger("link")
  }

  const linkField = (
    <>
      <NodePropertiesAccordion.LinkField
        node={nodeBasedOnForm}
        control={form.control}
        name="link"
        isFormDisabled={form.formState.disabled}
        onRemove={handleRemoveLink}
      />
      <NodeLinkDialog onSave={handleSaveLink} />
    </>
  )

  switch (updatedNode.type) {
    case NodeType.ForecastTable:
    case NodeType.ScenarioTable:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <NodePropertiesAccordion.ExportableField />
        </NodePropertiesAccordion>,
      )
      fields.push(
        <NodeMnemonicTableAccordion
          nodeId={updatedNode.id}
          nodeType={updatedNode.type}
          onPropertiesUpdate={handleTableAttributesUpdate}
        />,
      )
      break
    case NodeType.Table:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode &&
        fields.push(<NodeTableAccordion onPropertiesUpdate={handleTableAttributesUpdate} />)
      break
    case NodeType.Group:
      fields.push(labelFieldWithErrors)
      fields.push(
        <PropertyField
          control={form.control}
          label="Comments"
          name="comments"
          multiline
          minRows={2}
        />,
      )
      fields.push(<PropertyColorField control={form.control} label="Color" name="color" />)
      break
    case NodeType.Categorical:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode &&
        fields.push(
          <CategoricalPropertiesTableAccordion
            nodeId={updatedNode.id}
            nodeProperties={updatedNode.data.originalNode.properties}
            onPropertiesUpdate={handlePropertiesUpdate}
          />,
        )
      break
    case NodeType.Continuous:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode &&
        fields.push(
          <ContinuousPropertiesTableAccordion
            node={updatedNode}
            onPropertiesUpdate={handlePropertiesUpdate}
          />,
        )
      break
    case NodeType.Deterministic:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode && fields.push(<NodeExpressionAccordion node={updatedNode} />)
      break
    case NodeType.Discrete:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode &&
        fields.push(
          <DiscretePropertiesTableAccordion
            onPropertiesUpdate={handlePropertiesUpdate}
            node={updatedNode}
          />,
        )
      break
    case NodeType.Empty:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      break
    case NodeType.Forecast:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <NodePropertiesAccordion.ExportableField />
        </NodePropertiesAccordion>,
      )
      fields.push(<ForecastAccordion node={updatedNode} />)
      break
    case NodeType.Lag: {
      fields.push(
        <LagLabelField
          control={form.control}
          name="label"
          additionalErrors={lagErrorMessages.map(({ msg }) => msg)}
        />,
        <TagsSuggestionsAlert />,
      )

      fields.push(nodeTypeField)

      fields.push(
        <PropertyField
          control={form.control}
          type="number"
          label="Lag"
          name="lagValue"
          additionalErrors={lagValueErrorMessages.map(({ msg }) => msg)}
        />,
      )

      if (doesNodeHaveLagParent(updatedNode, allNodes)) {
        fields.push(
          <NodePropertiesAccordion>
            <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
            <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
          </NodePropertiesAccordion>,
        )
        fields.push(<NodeExpressionAccordion node={updatedNode} />)
      } else {
        const historicMnemonic = form.watch("historic_mnemonic")

        fields.push(
          <NodePropertiesAccordion>
            <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
            <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
            <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
            <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
            <NodePropertiesAccordion.DescriptionField />
            <NodePropertiesAccordion.CommentsField />
            <NodePropertiesAccordion.ExportableField />
          </NodePropertiesAccordion>,
        )

        fields.push(<>{!historicMnemonic && <SampleValueAccordion />}</>)
      }
      break
    }
    case NodeType.Mixture:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          {!hasLinkedNode && (
            <>
              <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
              <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
              <NodePropertiesAccordion.DescriptionField />
              <NodePropertiesAccordion.CommentsField />
              <NodePropertiesAccordion.ExportableField />
            </>
          )}
          {linkField}
        </NodePropertiesAccordion>,
      )
      !hasLinkedNode &&
        fields.push(
          <ContinuousPropertiesTableAccordion
            node={updatedNode}
            onPropertiesUpdate={handlePropertiesUpdate}
          />,
        )
      break
    case NodeType.Scenario:
      fields.push(labelFieldWithErrors)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion>
          <NodePropertiesAccordion.NodeTagsField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.HistoricMnemonicField nodeId={updatedNode.id} />
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <NodePropertiesAccordion.ExportableField />
        </NodePropertiesAccordion>,
      )
      fields.push(<ScenarioAccordion node={updatedNode} />)
      break
  }

  const handleFormKeyDown = (event: ReactKeyboardEvent) => {
    // We want to prevent submitting form by hitting "Enter" as you can do it while changing field
    // that has impact after clicking away from it.
    if (event.key === "Enter") event.preventDefault()
  }

  const handleSubmitError = () => {
    scrollToFirstFieldError()
  }

  return (
    <>
      <FormProvider {...form}>
        <KeyBinding
          bind={shortcuts["save-form"]}
          onKeyDown={form.handleSubmit(handleSubmit, handleSubmitError)}
          global
        />
        <form
          autoComplete={"off"}
          onSubmit={form.handleSubmit(handleSubmit, handleSubmitError)}
          id="node-form"
          onKeyDown={handleFormKeyDown}
        >
          <Grid container item direction="column" xs gap={1} sx={{ p: 0 }}>
            {fields.map((field, i) => (
              <Grid item key={i} sx={{ p: 0, m: 0, maxWidth: "100% !important" }}>
                {field}
              </Grid>
            ))}
          </Grid>
        </form>
      </FormProvider>

      {updatedNode.type !== NodeType.Group &&
        updatedNode.type !== NodeType.Empty &&
        updatedNode.type !== NodeType.ForecastTable &&
        updatedNode.type !== NodeType.ScenarioTable && (
          <Grid container item direction="column" gap={1} py={1}>
            <Grid item>
              <GraphNavGroup node={node} />
            </Grid>
            <Grid item>
              <GraphNavParents node={node} disabled={isDisabled} />
            </Grid>
            <Grid item>
              <GraphNavChildrens node={node} disabled={isDisabled} />
            </Grid>
          </Grid>
        )}

      {updatedNode.type === NodeType.Group && (
        <Grid container item direction="column" gap={1} py={1}>
          <Grid item>
            <GraphNavGroupsWithNodes node={node} />
          </Grid>
        </Grid>
      )}

      {updatedNode.type === NodeType.Empty && (
        <Grid container item gap={1} py={1}>
          <Grid item>
            <Tooltip title="Node drawer can not be closed until you decide what node type should be.">
              <Warning color="warning" />
            </Tooltip>
          </Grid>
          <Grid item>
            <Typography>You must choose node type before continuing.</Typography>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default NodeForm

// filter out options that sometimes won't be available as node type option, add them based on needs
const nodeTypeOptions = Object.values(NodeType).filter(
  (type) => ![NodeType.Group, NodeType.Mixture, NodeType.Empty, NodeType.Continuous].includes(type),
)
